import { Button, Typography } from '@breetometer/components';
import { StatusIcon } from 'BreetAssets';
import {
	AllWalletBalances,
	ContentWrapper,
	DataTable,
	HeaderCTA,
	type ITableColumn,
	sanitizeAndCheckEmpty,
	Seo,
	TransactionsMobileColumn,
} from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	type CURRENCYCODETYPE,
	formatDateTime,
	getTransactionStatus,
	type ITransactionHistory,
	SORT_LATEST_TO_OLDEST,
	type TransactionHistoryType,
} from 'BreetHelpers';
import { withPaginatedFilters, type WithPaginatedFiltersProps } from 'BreetHOC';
import { useComponentVisibilityApi, useCurrencyOutput, useWindowSize } from 'BreetHooks';
import { apiSlice, openModal, useAppDispatch, useGetTransactionsEditServicesQuery } from 'BreetRedux';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useMemo, useState } from 'react';

import WithdrawalFilter from './components/Filter';
import TableColumnDestination from './components/table-columns/Destination';
import TableColumnTransactionDetails from './components/table-columns/TransactionDetails';
import TableColumnType from './components/table-columns/Type';
import type { IWalletTransactionProps } from './WalletTransactions.helpers';

const WalletsTransactions = ({
	reqFilter,
	reqSearch,
	reqSorting,
	reqPageNum,
	reqPageSize,
	handleReqSearch,
	handlePageChange,
	handleSelectedFilterChange,
	filterProps,
	pageTitle,
	hideWalletBalance,
	seoPage,
	tip,
}: Partial<WithPaginatedFiltersProps<ITransactionHistory<TransactionHistoryType>>> & IWalletTransactionProps) => {
	const [settlementValue, setSettlementValue] = useState<string>('');
	const [statusValue, setStatusValue] = useState<string>('');

	const dispatch = useAppDispatch();

	const filterData = useMemo(
		() => ({
			page: reqPageNum,
			size: reqPageSize,
			defaultParams: !settlementValue ? '?services=withdrawals&services=edits&services=conversions&services=transfers' : '',
			filter: sanitizeAndCheckEmpty({ ...reqFilter, ...reqSearch, ...filterProps }),
			sort: sanitizeAndCheckEmpty({ ...reqSorting, createdAt: reqSorting?.createdAt ?? SORT_LATEST_TO_OLDEST }),
		}),
		[reqPageNum, reqPageSize, reqFilter, reqSearch, reqSorting, filterProps, settlementValue]
	);

	const {
		isLoading: withdrawalHistoryLoading,
		isFetching: withdrawalHistoryFetching,
		isError: withdrawalHistoryIsError,
		data: withdrawalHistoryData,
	} = useGetTransactionsEditServicesQuery(
		settlementValue
			? {
					...filterData,
					services: settlementValue,
				}
			: filterData,
		alwaysRefetchApiQueryOptions
	);

	const withdrawalHistory = withdrawalHistoryData?.data ?? [];

	const windowSize = useWindowSize();

	const currencyOutput = useCurrencyOutput();

	const isMobile = windowSize.isClient && windowSize.width <= 768;

	const viewWithdrawalHistoryDetails = useCallback(
		(id: string) => () => {
			dispatch(openModal({ id: 'withdrawalHistoryDetails', metadata: { id }, variant: 'right-sheet' }));
		},
		[dispatch]
	);

	const { hideComponentOnDesktop, hideComponentOnMobile } = useComponentVisibilityApi();

	const handleTableRowClick = useCallback(
		({ row }: { row: ITransactionHistory<TransactionHistoryType> }) => {
			viewWithdrawalHistoryDetails(row._id)();
		},
		[viewWithdrawalHistoryDetails]
	);

	const transactionTableCol = useMemo<ITableColumn<ITransactionHistory<TransactionHistoryType>>[]>(
		() => [
			{
				key: 'doc._id',
				searchKey: 'docId',
				title: 'Reference',
				copy: true,
				copyMessage: 'Reference copied successfully',
				onSearch: handleReqSearch,
				onClick: handleTableRowClick,
				...hideComponentOnMobile,
				width: 230,
			},
			{
				key: 'doc.meta.accountNumber',
				title: 'Destination',
				render: ({ row }) => <TableColumnDestination row={row} />,
				onClick: handleTableRowClick,
				...hideComponentOnMobile,
				width: 320,
			},
			{
				key: 'amount',
				title: 'Amount',
				width: 80,
				...hideComponentOnMobile,
				onClick: handleTableRowClick,
				render: ({ row }) => (
					<div className='transaction-payout-cell'>
						<Typography as='p'>{currencyOutput(row.amount)}</Typography>
						<Typography
							as='span'
							className='color-primary-50'
						>
							{row.currency.toUpperCase()}
						</Typography>
					</div>
				),
			},
			{
				key: 'doc.meta.type',
				title: 'Type',
				onClick: handleTableRowClick,
				...hideComponentOnMobile,
				render: ({ row }) => <TableColumnType row={row} />,
			},
			{
				key: 'status',
				title: 'Status',
				onClick: handleTableRowClick,
				render: ({ row }) => <StatusIcon status={getTransactionStatus(row.status)} />,
				...hideComponentOnMobile,
			},
			{
				key: 'createdAt',
				title: 'Created On',
				onClick: handleTableRowClick,
				render: ({ row }) => <Typography as='p'>{formatDateTime(row.createdAt)}</Typography>,
				width: 200,
				...hideComponentOnMobile,
			},
			{
				key: 'updatedAt',
				title: 'Completed On',
				onClick: handleTableRowClick,
				render: ({ row }) => <Typography as='p'>{formatDateTime(row.updatedAt)}</Typography>,
				width: 200,
				...hideComponentOnMobile,
			},
			{
				key: '_id',
				width: 200,
				onClick: handleTableRowClick,
				render: ({ row }) => (
					<TableColumnTransactionDetails
						row={row}
						onColumnClick={viewWithdrawalHistoryDetails(row._id)}
					/>
				),
				...hideComponentOnDesktop,
			},
			{
				key: 'status',
				onClick: handleTableRowClick,
				render: ({ row }) => (
					<TransactionsMobileColumn.Right
						amount={currencyOutput(row.amount)}
						currency={row.currency.toUpperCase() as CURRENCYCODETYPE}
						status={getTransactionStatus(row.status)}
						onClick={viewWithdrawalHistoryDetails(row._id)}
					/>
				),
				...hideComponentOnDesktop,
			},
			{
				key: 'extra',
				render: ({ row }) => (
					<div className='table-button'>
						<Button
							label='View Details'
							onClick={viewWithdrawalHistoryDetails(row._id)}
							variant='tertiary'
							className='theme-background-none border-tertiary-400'
						/>
					</div>
				),
				...hideComponentOnMobile,
			},
		],
		[
			currencyOutput,
			handleReqSearch,
			handleTableRowClick,
			hideComponentOnDesktop,
			hideComponentOnMobile,
			viewWithdrawalHistoryDetails,
		]
	);

	const isSearchFilter = !isEmpty(reqSearch) || !isEmpty(reqFilter);

	const onRefresh = useCallback(() => {
		dispatch(apiSlice.util.invalidateTags(['AllTransactions', 'GetUserWallets']));
	}, [dispatch]);

	const renderFilterDropdownWithLocalState = useCallback(
		(toggleVisibility?: () => void) => (
			<WithdrawalFilter
				handleSelectedFilterChange={handleSelectedFilterChange}
				settlementValue={settlementValue}
				setSettlementValue={setSettlementValue}
				setStatusValue={setStatusValue}
				statusValue={statusValue}
				toggleVisibility={toggleVisibility}
			/>
		),
		[handleSelectedFilterChange, settlementValue, statusValue]
	);

	return (
		<>
			<Seo page={seoPage} />

			<div className='datatable bodycontent'>
				{!hideWalletBalance && (
					<>
						<HeaderCTA /> <AllWalletBalances />
					</>
				)}

				<ContentWrapper
					hideBreadcrumb
					className={hideWalletBalance ? '' : 'wallet-transaction-table'}
				>
					<DataTable
						columns={transactionTableCol}
						data={withdrawalHistoryIsError ? [] : withdrawalHistory}
						isLoading={withdrawalHistoryLoading}
						hideHeader={isMobile}
						onPageChange={handlePageChange}
						paginate={withdrawalHistoryData?.meta}
						docItemsIdentifier='transactions'
						isSearchFilter={isSearchFilter}
						onRefresh={onRefresh}
						tip={tip}
						headerProps={{
							pageTitle,
							isFilter: !isEmpty(reqFilter),
							filterProps: {
								dropDownComponent: renderFilterDropdownWithLocalState,
							},
							isLoading: withdrawalHistoryLoading || withdrawalHistoryFetching,
							exportProps: {
								onClick: () => {
									// Implement export functionality
								},
							},
							searchProps: {
								handleReqSearch,
								placeHolder: 'Search by reference and type',
								searchKey: 'reference',
							},
						}}
					/>
				</ContentWrapper>
			</div>
		</>
	);
};

const WalletsWithHandlers = withPaginatedFilters(WalletsTransactions);

export default WalletsWithHandlers;
